<script setup lang="ts">
interface TabProps {
	label: string;
	id: string;
	iconName?: string;
}

const props = defineProps<{
	tabs: TabProps[];
	activeTabId: string;
	disabled?: boolean;
}>();

const emit = defineEmits<{ (e: 'selectTab', tabId: string): void }>();

const handleTabClick = (tabId: string) => {
	if (tabId !== props.activeTabId) emit('selectTab', tabId);
};
</script>

<template>
	<div class="tabs" data-ts="tabs">
		<button
			v-for="tab in tabs"
			:key="tab.id"
			type="button"
			:data-ts="`tab-${tab.id}`"
			:disabled="disabled"
			tabindex="0"
			:class="[
				'tabs__tab',
				{
					'tabs__tab--current': tab.id === activeTabId,
					'tabs__tab--disabled': disabled,
				},
			]"
			@click="handleTabClick(tab.id)"
		>
			<span v-if="tab.iconName" :class="`bx ${tab.iconName} mr-1 text-md`" />
			{{ tab.label }}
		</button>
	</div>
</template>

<style lang="scss" scoped>
.tabs {
	box-shadow: inset 0 -1px $color-gray-200;

	&__tab {
		font-size: $font-size;
		font-weight: $font-weight-medium;
		padding: $spacing-2 $spacing-1;
		margin-right: $spacing-6;
		transition: box-shadow 300ms ease-in;
		color: $color-gray-600;

		&:last-of-type {
			margin-right: 0;
		}

		&--current {
			color: $color-gray-950;
			box-shadow: inset 0 -2px $color-gray-950;
		}

		&--disabled {
			color: $color-gray-400;
			cursor: not-allowed;
		}

		@media (hover: hover) {
			&:hover:not([disabled]),
			&:focus-visible:not([disabled]) {
				box-shadow: inset 0 -2px rgba($color-neutral-500, 0.5);
			}
		}

		&:focus-visible:not([disabled]) {
			border-radius: $border-radius-2;
			outline: $border-width-2 $border-style $color-focus;
			box-shadow: inset 0 -2px rgba($color-gray-500, 0.5);
		}
	}
}
</style>
